/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import InputGroup from '@react/react-spectrum/InputGroup';
import TagField from '@react/react-spectrum/TagField';
import { Tag } from '@react/react-spectrum/TagList';

import { trackEvent } from '../../utils/tracking';
import {
    event_categoryfield_loaded,
    event_categorypicker_cancel,
    event_categorypicker_submit
} from '../../utils/trackingEvents';
import PickerButton from '../common/PickerButton';
import { useSelectionContext } from '../common/SelectionContext';
import { useCategoryPickerContext } from '../CategoryPicker/CategoryPickerContext';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import ReferenceTable from '../common/ReferenceTable';
import { useNavigate } from '../common/useNavigate';
import { properties_category } from '../ProductAdmin/urls';
import ReferenceButton from '../common/ReferenceButton';

import classes from './CategoryField.scss';

// i18n
const messages = defineMessages({
    addCategory: {
        id: 'dialog.addCategory',
        defaultMessage: 'Add Category'
    },
    viewCategory: {
        id: 'label.viewVategory',
        defaultMessage: 'View Category'
    }
});

const CategoryField = props => {
    const { multiple, selectionId, showLink } = props;
    const [schemaStatus, , [config]] = useGraphqlSchemaContext();
    const [
        { fieldSelection, loading },
        { applyPickerSelection, resetPickerSelection, addToFieldSelection, removeFromFieldSelection }
    ] = useSelectionContext();
    const [, { getSelectionId, getSelectionIdType }] = useCategoryPickerContext();
    const { formatMessage } = useIntl();
    const { open } = useNavigate();

    const disabled = loading || schemaStatus !== 'done' || props.disabled;

    const onChange = selection => {
        // Add new item
        if (selection.length > fieldSelection.length) {
            // Item to add is always the last one in the given list
            const keyToAdd = selection[selection.length - 1];
            addToFieldSelection([keyToAdd]);
        }

        // Remove existing item
        if (selection.length < fieldSelection.length) {
            // Find key that was deleted
            const keyToDelete = fieldSelection.find(category => {
                return selection.indexOf(category.label) === -1;
            });

            if (keyToDelete && keyToDelete.key) {
                removeFromFieldSelection(keyToDelete.key);
            }
        }
    };

    const renderInputFields = () =>
        fieldSelection.map(category => {
            const [value] = getSelectionId(category, selectionId, config.supportUID);
            return <input type="hidden" key={`${value}-input`} name={props.name} value={value} />;
        });

    const renderLinkCondition =
        showLink &&
        !loading &&
        schemaStatus === 'done' &&
        fieldSelection.length > 0 &&
        config.identifier &&
        config.identifier !== 'invalid';
    const renderReferenceButtonCondition = renderLinkCondition && !multiple && !fieldSelection[0].unknown;

    const pickerButton = (
        <PickerButton
            dialogTitle={formatMessage(messages.addCategory)}
            disabled={props.disabled}
            position={renderReferenceButtonCondition ? 'center' : 'right'}
            onClose={() => {
                trackEvent(event_categorypicker_cancel);
                resetPickerSelection();
            }}
            onConfirm={() => {
                trackEvent(event_categorypicker_submit);
                applyPickerSelection();
            }}
            testId="button-open-category-picker"
        >
            {React.cloneElement(props.children, {
                allowsMultipleSelection: multiple
            })}
        </PickerButton>
    );

    const renderTag = (tag, index) => {
        return (
            <Tag invalid={tag.unknown} key={index}>
                {tag.label}
            </Tag>
        );
    };

    const linkMapper = item =>
        open(
            '/commerce.html' +
                properties_category(config.identifier, item.uid && config.supportUID ? item.uid : item.id)
        );

    const renderReferenceTable = () => {
        if (renderLinkCondition && multiple) {
            const keyField = getSelectionIdType(config.supportUID, selectionId);

            return (
                <ReferenceTable
                    keyField={keyField}
                    keyFieldKey="selectionIdKey"
                    items={fieldSelection}
                    onView={linkMapper}
                />
            );
        }
        return null;
    };

    const renderReferenceButton = () => {
        if (renderReferenceButtonCondition) {
            const item = fieldSelection[0];
            return <ReferenceButton onPress={() => linkMapper(item)} label={formatMessage(messages.viewCategory)} />;
        }
        return null;
    };

    useEffect(() => {
        // Deprecation notice
        if (['idAndUrlPath', 'uidAndUrlPath'].includes(selectionId)) {
            console.warn(
                '[Deprecation] The selection-id value of "%s" is deprecated. Please use "urlPath" instead.',
                selectionId
            );
        }

        trackEvent(event_categoryfield_loaded);
    }, []);

    const allowCreate = multiple || fieldSelection.length === 0;

    return (
        <div className={classes.categoryfield}>
            <InputGroup
                className={[
                    classes.multiple_container,
                    '_spectrum-InputGroup_d053e',
                    classes.input_group,
                    props.inputClasses
                ].join(' ')}
            >
                <TagField
                    className={['spectrum-InputGroup-field', classes.autocomplete].join(' ')}
                    // Only allow to manually create a new selection if either multiple values are allowed or currently no value was selected
                    // Also set the readOnly prop here, which only affects the TextField component, not the overall TagField
                    allowCreate={allowCreate}
                    readOnly={!allowCreate}
                    placeholder={props.placeholder}
                    // We need to create a unique label here, since the TagField component deletes by label and there might be duplicates
                    value={fieldSelection}
                    disabled={disabled}
                    aria-labelledby={props.labelledBy}
                    onChange={onChange}
                    renderTag={renderTag}
                    invalid={props.invalid}
                />
                {pickerButton}
                {renderReferenceButton()}
                {props.name && renderInputFields()}
            </InputGroup>
            {renderReferenceTable()}
        </div>
    );
};

CategoryField.propTypes = {
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    labelledBy: PropTypes.string,
    inputClasses: PropTypes.string,
    name: PropTypes.string,
    multiple: PropTypes.bool,
    invalid: PropTypes.bool,
    selectionId: PropTypes.oneOf(['id', 'slug', 'idAndUrlPath', 'uid', 'uidAndUrlPath', 'urlPath']),
    showLink: PropTypes.bool
};

CategoryField.defaultProps = {
    placeholder: 'Category',
    multiple: false,
    inputClasses: '',
    showLink: false
};

export default CategoryField;
